<template>
  <div class="w-base-level-table-look-a-like-unit d-flex flex-column">
    <v-expansion-panels
      v-if="showFilter"
      v-model="collapsePanel"
    >
      <v-expansion-panel>
        <w-expansion-panel-header class="w-expansion-panel-header">
          <span>{{ $t('Common.filter&GroupBy') }}</span>
          <template #actions>
            <BaseLevelFilterHeaderContainer
              :allow-change-filters.sync="allowChangeFilters"
              :allow-presets="allowPresets"
              :preset.sync="preset"
              :filter-card-state.sync="filterCardState"
              :concept="concept"
              :selected-rows.sync="selectedRows"
              :headers="internalHeaders"
              :orderd-filters.sync="orderdFilters"
              :not-visible-filters.sync="notVisibleFilters"
              :available-presets="availablePresets"
              :enable-create-new-data="enableCreateNewData"
              @new:clicked="invokeNewAction"
            >
              <template slot="searchFilter">
                <slot name="searchFilter" />
              </template>

              <template slot="groupSearchFilter">
                <slot name="groupSearchFilter" />
              </template>
            </BaseLevelFilterHeaderContainer>
          </template>
        </w-expansion-panel-header>

        <w-expansion-panel-content class="w-expansion-panel-content">
          <BaseLevelFilterContainer
            :concept="concept"
            :does-table-exists="tableExists"
            :filter-card-state.sync="filterCardState"
            :filters="filters"
            :get-table-state="getCachedTable"
            :is-default.sync="presetIsDefault"
            :name.sync="presetName"
            :not-visible-filters.sync="notVisibleFilters"
            :orderd-filters.sync="orderdFilters"
            :preset.sync="preset"
            :save-columns.sync="presetSaveColumns"
            :set-preset-obj="setCachedPresetObj"
            @set:filter="invokeSetCachedFilterValue"
          >
            <template slot="prependSearchFilter">
              <slot name="prependSearchFilter" />
            </template>

            <template slot="searchFilter">
              <slot name="searchFilter" />
            </template>

            <template slot="groupSearchFilter">
              <slot name="groupSearchFilter" />
            </template>
          </BaseLevelFilterContainer>
        </w-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <BaseLevelTableContainer
      v-if="value != undefined"
      ref="baseLevelTableContainer"
      :filters="filters"
      :get-data="getData"
      :headers="headers"
      :initial-headers="headers"
      :hide-headers="hideHeaders"
      :invoked-param-handler="invokedParamHandler"
      :is-clickable="isClickable"
      :not-visible-headers="notVisibleHeaders"
      :number-of-entries-colspan="numberOfEntriesColspan"
      :with-total-hits="withTotalHits"
      :orderd-headers="orderdHeaders"
      :path="path"
      :search="search"
      :selected="selectedRows"
      :show-select="showSelect"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      :with-default-footer="withDefaultFooter"
      :truncate-data="truncateData"
      :truncate-options="truncateOptions"
      :mandatory-filters="mandatoryFilters"
      :item-class="itemClass"
      :has-max-height="hasMaxHeight"
      :group-by="groupBy"
      toggle-force-reload
      @input="(newValue) => $emit('input', newValue)"
      @click:row="invokeHandleRowClick"
      @update:options="invokeSetCachedSort"
    >
      <template #top>
        <slot name="top" />
      </template>

      <template #top-title>
        {{ name }}
      </template>

      <template #table-top-actions>
        <slot name="table-top-actions" />
        <w-table-action-wrapper>
          <BaseLevelTableHeaderContainer
            :allow-redirect-to-search="allowRedirectToSearch"
            :allow-add="isAddable"
            :allow-change-table-headers="isHeaderChangeable"
            :allow-download="allowDownload"
            :allow-zip-download="allowZipDownload"
            :allow-csv-download="allowCsvDownload"
            :allow-column-mode-switch="allowColumnModeSwitch"
            :preset.sync="preset"
            :actions="actionArray"
            :headers="internalHeaders"
            :orderd-headers.sync="orderdHeaders"
            :not-visible-headers.sync="notVisibleHeaders"
            :truncate-data.sync="truncateData"
            :truncate-options.sync="truncateOptions"
            :enable-create-new-data="enableCreateNewData"
            :showBulkSelectAction="showBulkSelectAction"
            @new:clicked="invokeNewAction"
            @export:clicked="exportData"
            @zip-export:clicked="zipExportData"
            @bulkSelectionTriggered="invokeBulkAction = true"
          >
            <template slot="inner-table-actions">
              <slot name="inner-table-actions" />
            </template>
          </BaseLevelTableHeaderContainer>
        </w-table-action-wrapper>
      </template>
      <template
        v-for="(_, name) in $scopedSlots"
        :slot="name"
        slot-scope="slotData"
      >
        <slot
          v-bind="slotData"
          :name="name"
        />
      </template>
    </BaseLevelTableContainer>
  </div>
</template>

<script>
//mixins
import workUnitMixin from '@/components/global_components/unit/work-unit-mixin';
import workUnitMobileMixin from './work-unit-mobile-mixin';
import mobileFilterMixin from '@/components/base_components/tables/filter/mobile-filter-mixin';
import baseLevelTableMixin from '@/components/view_root_components/base_level_table_module/mixins/base-level-table-mixin';
import baseLevelTableCacheHandlerMixin from '@/components/view_root_components/base_level_table_module/mixins/base-level-table-cache-handler-mixin';
//helpers & services
import BASE_LEVEL_TABLE_MODULE_DEFAULT_PROPS from '@/components/view_root_components/base_level_table_module/enums/BASE_LEVEL_TABLE_MODULE_DEFAULT_PROPS';
import isEqual from 'lodash/isEqual';
import LOOK_A_LIKE_FEATURE_FLAG_PROPS from '@/components/global_components/tables/base-table-look-a-like/enums/LOOK_A_LIKE_FEATURE_FLAG_PROPS';
import lookALikeGettersAndSetter from '@/components/global_components/tables/base-table-look-a-like/mixins/look-a-like-cache-getters-and-setters';
import tableStoreService from '@/components/base_components/tables/services/table-store-service';
import TABLE_DATA_KEYS from '@/components/base_components/tables/enum/table-data-keys';
import { mapMutations } from 'vuex';
//components
import BaseLevelFilterContainer from '@/components/view_root_components/base_level_table_module/components/BaseLevelFilterContainer';
import BaseLevelFilterHeaderContainer from '@/components/view_root_components/base_level_table_module/components/BaseLevelFilterHeaderContainer';
import BaseLevelTableHeaderContainer from '@/components/view_root_components/base_level_table_module/components/BaseLevelTableHeaderContainer';
import BaseLevelTableContainer from '@/components/view_root_components/base_level_table_module/components/BaseLevelTableContainer';
import i18n from '@/language/i18n';

export default {
  name: 'WorkBaseLevelTableLookALikeUnit',
  components: {
    BaseLevelTableContainer,
    BaseLevelFilterContainer,
    BaseLevelFilterHeaderContainer,
    BaseLevelTableHeaderContainer,
  },
  mixins: [
    workUnitMixin,
    workUnitMobileMixin,
    mobileFilterMixin,
    baseLevelTableMixin(
      BASE_LEVEL_TABLE_MODULE_DEFAULT_PROPS,
      LOOK_A_LIKE_FEATURE_FLAG_PROPS,
      baseLevelTableCacheHandlerMixin(lookALikeGettersAndSetter)
    ),
  ],
  data() {
    return {
      fields: undefined,
      collapsePanel: 0,
      invokeBulkAction: false
    };
  },
  props: {
    title: { type: String },
    noHeader: { type: Boolean, default: false },
    value: { required: true },
    concept: { type: String, required: true },
    selectedRows: { type: Array, default: () => [] },
    initialSortBy: { type: Array, required: false },
    initialSortDesc: { type: Array, required: false },
    hasMaxHeight: { type: Boolean, default: false },
    allowDownload: { type: Boolean, default: true },
    allowZipDownload: { type: Boolean, default: true },
    allowCsvDownload: { type: Boolean, default: false },
    showFilter: { type: Boolean, default: true },
    enableCreateNewData: { type: Boolean, default: true },
    createDatasetTooltip: {
      type: String,
      default: i18n.t('Components.sentences.createNewDataSet'),
    },
    groupBy: { type: String, required: false },
    showBulkSelectAction: { type: Boolean, default: false }
  },
  computed: {
    panelCollapseOnXS() {
      if (this.$vuetify.breakpoint.xsOnly) return;
      else return 0;
    },
  },
  watch: {
    panelCollapseOnXS() {
      this.collapsePanel = this.panelCollapseOnXS;
    },
    value: {
      async handler(newValue) {
        if (newValue !== undefined)
          return;

        const tableWrapper = tableStoreService.generateTableObject({
          [TABLE_DATA_KEYS.FILTERS]: this.initialFilter,
        });

        const subTable = tableStoreService.generateSubTableObject({
          [TABLE_DATA_KEYS.SORT_BY]: this.initialSortBy,
          [TABLE_DATA_KEYS.SORT_BY_DESC]: this.initialSortDesc,
          [TABLE_DATA_KEYS.HEADER_ORDER]: this.internalHeaders.map(header => header.value),
          [TABLE_DATA_KEYS.NOT_VISIBLE_HEADERS]: this.getHiddenHeaders(),
        });

        await this.$emit('input',
          Object.assign(tableWrapper, subTable)
        );

        await this.loadPresetsAndSetDefault();
      },
      immediate: true,
    },
    'filters.search'(newSearchValue) {
      this.invokeSetCachedSearch(newSearchValue);
    },
    initialFilter(newInitialFilterValue, oldVal) {
      if (this.preset?.id || isEqual(newInitialFilterValue, oldVal)) return;
      for (const [key, value] of Object.entries(newInitialFilterValue)) {
        this.invokeSetCachedFilterValue({ key, value });
      }
    },
    invokeBulkAction(val) {
      if (val) {
        this.$emit('triggerBulkConfirmAction');
        this.invokeBulkAction = false;
      }
    }
  },
  methods: {
    ...mapMutations(['setActiveModuleSearchable', 'setActiveModuleName', 'setActiveModuleSubtitle']),
    reloadTable() {
      this.$refs.baseLevelTableContainer.reloadTable();
    },
  },
  mounted() {
    this.setActiveModuleSearchable(true);
  },
};
</script>

<style lang="scss" scoped>
@import 'src/components/global_components/unit/WUnit';

.w-base-level-table-look-a-like-unit ::v-deep {
  height: 100%; // scrollbar on table

  @media #{map-get($display-breakpoints, 'xs-only')} {
    height: auto; // scrollbar on main content
  }

  // overwrites default style of w-card
  .table-wrapper-card {
    margin: 0rem;
    padding: 0;
    transition: all 100ms ease 0s;
  }
}

.v-expansion-panels ::v-deep .v-expansion-panel {
  border-radius: $w-unit-radius !important;
  background: none !important;
  margin-top: 0.5 px;
  margin-bottom: $grid-gutter;
}

.v-expansion-panels ::v-deep .v-expansion-panel--active .w-expansion-panel-header {
  border-bottom-left-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}

.w-expansion-panel-header {
  width: 100%;
  background-color: var(--v-work_card-base);
  border-radius: $w-unit-radius;
}

.w-expansion-panel-content ::v-deep .v-expansion-panel-content__wrap {
  $content-padding: 0;
  background-color: var(--v-work_card-base);
  padding: 0.25rem 0 0 0 !important;
  border-bottom-left-radius: $w-unit-radius;
  border-bottom-right-radius: $w-unit-radius;
  overflow: hidden;
}

.v-expansion-panel-content__wrap>div {
  margin-top: -1rem !important;
}
</style>
